import { HTMLAttributes, forwardRef, ReactNode, useState, createContext, useContext } from 'react';

import { cn } from '../utils/cn';

// Sidebar Context
interface SidebarContextValue {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function useSidebar() {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('Sidebar components must be used within a Sidebar provider');
  }
  return context;
}

// Sidebar Root
export interface SidebarProps extends HTMLAttributes<HTMLElement> {
  defaultCollapsed?: boolean;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  variant?: 'default' | 'floating';
}

export const Sidebar = forwardRef<HTMLElement, SidebarProps>(
  (
    {
      className,
      defaultCollapsed = false,
      collapsed: controlledCollapsed,
      onCollapsedChange,
      variant = 'default',
      children,
      ...props
    },
    ref
  ) => {
    const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
    const collapsed = controlledCollapsed ?? internalCollapsed;

    const setCollapsed = (value: boolean) => {
      if (controlledCollapsed === undefined) {
        setInternalCollapsed(value);
      }
      onCollapsedChange?.(value);
    };

    return (
      <SidebarContext.Provider value={{ collapsed, setCollapsed }}>
        <aside
          ref={ref}
          className={cn(
            'flex flex-col h-full transition-all duration-300 ease-in-out',
            {
              'w-64': !collapsed,
              'w-20': collapsed,
              'bg-[color:var(--ids-bg-subtle,#0F1626)] border-r border-[color:var(--ids-border,rgba(255,255,255,0.08))]':
                variant === 'default',
              'bg-[color:var(--ids-glass-bg,rgba(20,27,45,0.72))] backdrop-blur-xl m-2 rounded-[var(--ids-radius-xl,20px)] border border-[color:var(--ids-border,rgba(255,255,255,0.08))]':
                variant === 'floating',
            },
            className
          )}
          {...props}
        >
          {children}
        </aside>
      </SidebarContext.Provider>
    );
  }
);

Sidebar.displayName = 'Sidebar';

// Sidebar Header
export interface SidebarHeaderProps extends HTMLAttributes<HTMLDivElement> {
  logo?: ReactNode;
  title?: string;
  subtitle?: string;
}

export const SidebarHeader = forwardRef<HTMLDivElement, SidebarHeaderProps>(
  ({ className, logo, title, subtitle, children, ...props }, ref) => {
    const { collapsed } = useSidebar();

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center gap-3 h-16 px-4 border-b border-[color:var(--ids-border,rgba(255,255,255,0.08))]',
          className
        )}
        {...props}
      >
        {children || (
          <>
            {logo && <div className="flex-shrink-0">{logo}</div>}
            {!collapsed && (title || subtitle) && (
              <div className="min-w-0 overflow-hidden">
                {title && (
                  <p className="text-sm font-semibold text-[color:var(--ids-text,#F8FAFC)] truncate">
                    {title}
                  </p>
                )}
                {subtitle && (
                  <p className="text-xs text-[color:var(--ids-text-subtle,#7E8DA6)] truncate">
                    {subtitle}
                  </p>
                )}
              </div>
            )}
          </>
        )}
      </div>
    );
  }
);

SidebarHeader.displayName = 'SidebarHeader';

// Sidebar Content (scrollable area)
export type SidebarContentProps = HTMLAttributes<HTMLDivElement>;

export const SidebarContent = forwardRef<HTMLDivElement, SidebarContentProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('flex-1 overflow-y-auto py-4 px-3', className)} {...props} />
  )
);

SidebarContent.displayName = 'SidebarContent';

// Sidebar Group
export interface SidebarGroupProps extends HTMLAttributes<HTMLDivElement> {
  label?: string;
}

export const SidebarGroup = forwardRef<HTMLDivElement, SidebarGroupProps>(
  ({ className, label, children, ...props }, ref) => {
    const { collapsed } = useSidebar();

    return (
      <div ref={ref} className={cn('mb-4', className)} {...props}>
        {label && !collapsed && (
          <p className="px-3 mb-2 text-[10px] font-semibold text-[color:var(--ids-text-subtle,#7E8DA6)] uppercase tracking-[0.08em]">
            {label}
          </p>
        )}
        <nav className="space-y-1">{children}</nav>
      </div>
    );
  }
);

SidebarGroup.displayName = 'SidebarGroup';

// Sidebar Item
export interface SidebarItemProps extends HTMLAttributes<HTMLButtonElement> {
  icon?: ReactNode;
  active?: boolean;
  disabled?: boolean;
  badge?: string | number;
  badgeColor?: 'default' | 'primary' | 'success' | 'warning' | 'danger';
  href?: string;
  as?: 'button' | 'a';
}

export const SidebarItem = forwardRef<HTMLButtonElement, SidebarItemProps>(
  (
    {
      className,
      icon,
      active,
      disabled,
      badge,
      badgeColor = 'default',
      href,
      as = 'button',
      children,
      onClick,
      ...props
    },
    ref
  ) => {
    const { collapsed } = useSidebar();

    const badgeColors = {
      default:
        'bg-[color:var(--ids-bg-subtle,#0F1626)] text-[color:var(--ids-text-muted,#94A3B8)] border border-[color:var(--ids-border,rgba(255,255,255,0.08))]',
      primary:
        'bg-[color:rgba(var(--ids-brand-rgb,59,130,246),0.16)] text-[color:var(--ids-brand,#3B82F6)]',
      success:
        'bg-[color:rgba(var(--ids-success-rgb,34,197,94),0.16)] text-[color:var(--ids-success,#22C55E)]',
      warning:
        'bg-[color:rgba(var(--ids-warning-rgb,245,158,11),0.16)] text-[color:var(--ids-warning,#F59E0B)]',
      danger:
        'bg-[color:rgba(var(--ids-danger-rgb,239,68,68),0.16)] text-[color:var(--ids-danger,#EF4444)]',
    };

    const Component = as === 'a' ? 'a' : 'button';
    const componentProps = as === 'a' ? { href } : { type: 'button' as const };

    return (
      <Component
        ref={ref as any}
        className={cn(
          'w-full flex items-center gap-3 px-3 py-2 rounded-[var(--ids-radius-md,10px)] text-sm font-medium transition-colors',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ids-focus,#22D3EE)]',
          {
            'justify-center': collapsed,
            'bg-[color:rgba(var(--ids-brand-rgb,59,130,246),0.14)] text-[color:var(--ids-brand,#3B82F6)]':
              active,
            'text-[color:var(--ids-text-muted,#94A3B8)] hover:bg-[color:var(--ids-surface,#141B2D)] hover:text-[color:var(--ids-text,#F8FAFC)]':
              !active && !disabled,
            'opacity-50 cursor-not-allowed': disabled,
          },
          className
        )}
        disabled={disabled}
        onClick={onClick}
        {...componentProps}
        {...(props as any)}
      >
        {icon && <span className="flex-shrink-0">{icon}</span>}
        {!collapsed && <span className="flex-1 truncate">{children}</span>}
        {!collapsed && badge !== undefined && (
          <span
            className={cn('px-2 py-0.5 text-xs font-medium rounded-full', badgeColors[badgeColor])}
          >
            {badge}
          </span>
        )}
      </Component>
    );
  }
);

SidebarItem.displayName = 'SidebarItem';

// Sidebar Footer
export type SidebarFooterProps = HTMLAttributes<HTMLDivElement>;

export const SidebarFooter = forwardRef<HTMLDivElement, SidebarFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'p-3 border-t border-[color:var(--ids-border,rgba(255,255,255,0.08))]',
        className
      )}
      {...props}
    />
  )
);

SidebarFooter.displayName = 'SidebarFooter';

// Sidebar Toggle Button
export type SidebarToggleProps = HTMLAttributes<HTMLButtonElement>;

export const SidebarToggle = forwardRef<HTMLButtonElement, SidebarToggleProps>(
  ({ className, ...props }, ref) => {
    const { collapsed, setCollapsed } = useSidebar();

    return (
      <button
        ref={ref}
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        className={cn(
          'p-2 text-[color:var(--ids-text-muted,#94A3B8)] hover:text-[color:var(--ids-text,#F8FAFC)] hover:bg-[color:var(--ids-surface,#141B2D)] rounded-[var(--ids-radius-md,10px)] transition-colors',
          className
        )}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        {...props}
      >
        <svg
          className={cn('w-5 h-5 transition-transform', collapsed && 'rotate-180')}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M11 19l-7-7 7-7m8 14l-7-7 7-7"
          />
        </svg>
      </button>
    );
  }
);

SidebarToggle.displayName = 'SidebarToggle';
